import { mapGetters } from 'vuex';
import md5 from 'crypto-js/md5';
import Popper from 'vue-popperjs';
import dayjs from 'dayjs';
import { BfModal, BfDialog, BfButton, BfDatepicker } from 'src/components/Butterfly';
import SimpleDropdown from 'components/widgets/SimpleDropdown.vue';
import PhraseDetectionDescription from 'components/project/analysis/create/widgets/PhraseDetectionDescription.vue';
import RemoveDuplicatesDescription from 'components/project/analysis/create/widgets/RemoveDuplicatesDescription.vue';
import ProgressIndicator from 'src/components/widgets/ProgressIndicator.vue';
import RadioButtons from 'src/components/widgets/RadioButtons.vue';
import Stopwords from 'components/project/analysis/results/widgets/Stopwords.vue';
import Project from 'src/api/project';
import Vue from 'vue';
import { CLEAR_ERRORS, CLEAR_REQUEST_ERRORS } from 'src/store/types';
import Multiselect from 'vue-multiselect';
import GeneralUtils from 'src/utils/general';
import FormatUtils from 'src/utils/formatters';
import { formatDrfErrors } from 'src/utils/display';
export default Vue.extend({
    components: {
        BfModal, BfDialog, BfButton, BfDatepicker,
        SimpleDropdown, Popper, ProgressIndicator, RadioButtons, Stopwords,
        PhraseDetectionDescription, RemoveDuplicatesDescription, Multiselect,
    },
    beforeRouteEnter(to, from, next) {
        Project.getStructuredDataFields(to.params.projectId)
            .then((response) => {
            const ops = {};
            for (const field of response) {
                ops[field] = {
                    visible: false,
                    loading: false,
                    segments: [],
                    selected: [],
                    fetched: false,
                };
            }
            next(vm => {
                vm.structuredFields = response;
                vm.segmentOptions = ops;
            });
        })
            .catch(() => {
            next(false);
        });
    },
    data() {
        return {
            loading: false,
            name: '',
            nameError: false,
            nameDuplicateError: false,
            otherServerError: undefined,
            other: false,
            pasteFiltersField: null,
            pasteFiltersModalVisible: false,
            phraseDetection: false,
            removeDuplicates: false,
            defaultDateField: null,
            automaticFrameworkGeneration: false,
            selectedDateField: null,
            selectedFields: [],
            settingsVisible: false,
            stemming: true,
            step: 1,
            structuredDataIndex: {},
            DATE_FORMAT: 'DD/MM/YYYY',
            // Actual date values
            dateTo: '',
            dateFrom: '',
            allTime: true,
            structuredFields: [],
            segmentOptions: [],
            segmentFilterChoices: [
                { label: 'Include all segments', value: 'all' },
                { label: 'Include only selected segments (choose)', value: 'include' },
                { label: 'Exclude the selected segments (choose)', value: 'exclude' }
            ],
            segmentFilterSelectedChoice: 'all',
            clusteringSettings: {
                phrase_coverage_cutoff: 0.005,
                cluster_all_phrases: false,
                tsne_settings: {
                    learning_rate: 200,
                    epochs: 1000,
                    momentum: 0.5,
                    final_momentum: 0.8,
                    stop_lying_epoch: 250,
                    embeddings_dim: 2,
                    perplexity: 2,
                    random_init: false,
                },
                clustering: {
                    type: 'agglomerative',
                    n_clusters: 16,
                    random_state: 42,
                    distance_threshold: 65,
                }
            }
        };
    },
    computed: {
        ...mapGetters([
            'currentProject', 'currentSite', 'featureFlags', 'currentUser',
        ]),
        validDates() {
            // if there is a from & a to date, from must be before the to
            let result = true;
            if (this.dateFrom && this.dateTo) {
                result = dayjs(this.dateFrom, this.DATE_FORMAT).isSameOrBefore(dayjs(this.dateTo, this.DATE_FORMAT));
            }
            return result;
        },
        noTextSelectedError() {
            return this.selectedFields.length === 0;
        },
        metaInfo() {
            return {
                title: this.currentProject ? `${this.currentProject.name} Analysis - Kapiche` : null
            };
        },
        dateFields() {
            return this.schemaFields.filter(field => {
                return field.type === Project.COLUMN_LABELED_TYPES.get('DATE') ||
                    field.type === Project.COLUMN_LABELED_TYPES.get('DATE_TIME');
            });
        },
        schemaFields() {
            return this.currentProject.schema.map((field) => {
                return {
                    index: true,
                    name: field.name,
                    type: field.type,
                    id: md5(field.name).toString() // something safe to use as a class name
                };
            });
        },
        selectedNames() {
            return this.selectedFields.map(id => this.textFields.find(f => f.id === id).name);
        },
        showSegmentsList() {
            return ['include', 'exclude'].includes(this.segmentFilterSelectedChoice);
        },
        segmentMultiselectPlaceholder() {
            const include = this.segmentFilterSelectedChoice === 'include';
            return `Choose segments to ${include ? 'include in' : 'exclude from'} analysis...`;
        },
        textFieldLabel() {
            return this.selectedNames.map(n => `"${n}"`).join(', ');
        },
        textFields() {
            return this.schemaFields.filter((field) => field.type === Project.COLUMN_LABELED_TYPES.get('TEXT'));
        },
        analysisWithThatNameAlreadyExists() {
            return this.currentProject.analyses.some(a => a.name === this.name);
        },
        selectedSegments() {
            const result = [];
            for (const [field, options] of Object.entries(this.segmentOptions)) {
                for (const option of options.selected)
                    result.push([field, option.value]);
            }
            return result;
        }
    },
    watch: {
        dateTo() { this.allTime = !(this.dateTo || this.dateFrom); },
        dateFrom() { this.allTime = !(this.dateTo || this.dateFrom); },
        allTime() {
            if (this.allTime) {
                this.dateTo = '';
                this.dateFrom = '';
            }
        },
    },
    mounted() {
        // Initialise default selected text field
        if (this.textFields.length === 1) {
            this.selectedFields.push(this.textFields[0].id);
        }
        if (this.dateFields.length > 0) {
            // Set the default date field based on the first field
            this.defaultDateField = this.dateFields[0].name;
            this.selectedDateField = this.dateFields[0].name;
        }
    },
    methods: {
        truncate: FormatUtils.truncate,
        back() {
            // reset errors
            this.nameError = false;
            this.nameDuplicateError = false;
            this.otherServerError = undefined;
            // if dates invalid, then clear them
            if (!this.validDates) {
                this.dateTo = '';
                this.dateFrom = '';
            }
            // go back
            this.step -= 1;
        },
        clearSegmentFilters() {
            for (const option of Object.values(this.segmentOptions)) {
                option.selected = [];
            }
        },
        createAnalysis() {
            this.$store.dispatch({ type: CLEAR_ERRORS });
            let postData = this._getPostData();
            this.loading = true;
            Project.createAnalysis(this.currentProject.id, postData)
                .then(response => {
                let eventData = {
                    phraseDetection: postData.phrase_detection,
                    removeDuplicates: postData.remove_duplicates,
                    stemming: postData.stemming,
                    stopwords: postData.stopwords,
                    numTextFields: this.selectedFields.length,
                    allTime: this.allTime,
                    dateFrom: null,
                    dateTo: null,
                    dateField: null
                };
                if (this.selectedDateField) {
                    eventData.dateField = postData.date_field;
                }
                if (!this.allTime) {
                    eventData.dateFrom = postData.date_from;
                    eventData.dateTo = postData.date_to;
                }
                this.$analytics.track.analysis.run(eventData);
                this.$router.push({ name: 'project-details', params: { id: this.currentProject.id } });
            })
                .catch((errors) => {
                let nonFieldErrors = errors.body['non_field_errors'] || [''];
                if (nonFieldErrors[0].toLowerCase().includes('name already exists')) {
                    this.$store.dispatch(CLEAR_REQUEST_ERRORS); // use custom instead of global error handling
                    this.nameDuplicateError = true;
                }
                else {
                    this.otherServerError = formatDrfErrors(errors?.body);
                }
                this.loading = false;
            });
        },
        loadInputFilters() {
            const segOpts = this.segmentOptions[this.pasteFiltersField];
            const value = this.$refs.filtersInput.value;
            let segments = GeneralUtils.parseSegmentsFromStr(value);
            segments.forEach(s => {
                if (segOpts.selected.indexOf(s) < 0) {
                    segOpts.selected.push({ label: s, value: s });
                }
            });
            this.pasteFiltersModalVisible = false;
        },
        proceed() {
            if (this.step === 1) {
                // Check that text field is selected
                if (this.noTextSelectedError)
                    return;
                this.step += 1;
            }
            else if (this.step === 2) {
                this.step += 1;
            }
            else {
                this.nameDuplicateError = false;
                this.nameError = false;
                if (this.name.length === 0) {
                    this.nameError = true;
                    return;
                }
                this.createAnalysis();
            }
        },
        closePasteFiltersModal() {
            this.pasteFiltersModalVisible = false;
        },
        showPasteFiltersModal(field) {
            this.pasteFiltersField = field;
            this.pasteFiltersModalVisible = true;
            return false;
        },
        async toggleSegments(field) {
            let options = this.segmentOptions[field];
            if (options.fetched) {
                options.visible = !options.visible;
                Vue.set(this.segmentOptions, field, options);
                return;
            }
            try {
                options.loading = true;
                options.visible = true;
                Vue.set(this.segmentOptions, field, options);
                const response = await Project.getStructuredDataSegments(this.currentProject.id, field);
                options.segments = response.map(seg => seg === '' ? { value: seg, label: '(No Value)' } : { value: seg, label: seg });
                options.fetched = true;
                Vue.set(this.segmentOptions, field, options);
            }
            finally {
                options.loading = false;
                Vue.set(this.segmentOptions, field, options);
            }
        },
        _getPostData() {
            let postData = {
                name: this.name,
                text_fields: this.selectedNames,
                phrase_detection: this.phraseDetection,
                stemming: this.stemming,
                automatic_framework_generation: this.automaticFrameworkGeneration,
                remove_duplicates: this.removeDuplicates,
                stopwords: JSON.stringify(this.$refs.stopwords.stopwordList),
                excluded_segments: [],
                included_segments: [],
                clustering_settings: this.automaticFrameworkGeneration ? this.clusteringSettings : {},
                automatic_theme_generation: this.automaticFrameworkGeneration,
            };
            // Currently, support for including or excluding
            // segments is mutually exclusive. This could be
            // changed by storing segments into separate exclude
            // & include lists that are always written to the
            // post data. This is supported by the backend.
            //
            // For now we selectively overwrite default empty
            // excluded or included segment lists on the post data
            // based on which radio button the user has selected.
            switch (this.segmentFilterSelectedChoice) {
                case 'include':
                    postData.included_segments = this.selectedSegments;
                    break;
                case 'exclude':
                    postData.excluded_segments = this.selectedSegments;
                    break;
                default:
                    break;
            }
            // Append dates only if the user has not selected "all time"
            // (dates restrict the analysis, alltime does not)
            //
            // TODO: it should not be required to also test the selected date
            //  field, but we've had a situation before where `allTime` was
            //  truthy and the selected date field was `null`. See ch10399.
            if (!this.allTime && this.selectedDateField) {
                // Parse dates from our known format before sending
                let dateToIso = this.dateTo !== '' ? dayjs.utc(this.dateTo, this.DATE_FORMAT).endOf("day").toISOString() : '';
                let dateFromIso = this.dateFrom !== '' ? dayjs.utc(this.dateFrom, this.DATE_FORMAT).startOf("day").toISOString() : '';
                // We are going to pass the relevant info to construct the date_query on the backend
                postData['date_to'] = dateToIso;
                postData['date_from'] = dateFromIso;
                postData['date_field'] = this.selectedDateField;
            }
            if (this.defaultDateField) {
                postData['default_date_field'] = this.defaultDateField;
            }
            return postData;
        }
    }
});
